import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanLine, Loader2, CameraOff } from "lucide-react";

// Extract the GB Asset ID from a scanned QR payload. Labels encode
// `<host>/a/<tag>`, but we also accept a raw tag typed/pasted in.
export function tagFromScan(text) {
  if (!text) return "";
  const s = String(text).trim();
  const m = s.match(/\/a\/([^/?#\s]+)/);
  if (m) {
    try { return decodeURIComponent(m[1]); } catch { return m[1]; }
  }
  return s;
}

export function ScanTagDialog({ open, onOpenChange, onResult }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [manual, setManual] = useState("");
  const [camState, setCamState] = useState("idle"); // idle | starting | scanning | unsupported | denied
  const supported = typeof window !== "undefined" && "BarcodeDetector" in window;

  useEffect(() => {
    if (!open) return;
    setManual("");
    if (!supported) { setCamState("unsupported"); return; }
    let cancelled = false;
    let detector;
    (async () => {
      try {
        setCamState("starting");
        detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" } });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setCamState("scanning");
        const tick = async () => {
          if (cancelled || !videoRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes && codes.length) {
              const tag = tagFromScan(codes[0].rawValue);
              if (tag) { finish(tag); return; }
            }
          } catch { /* transient decode errors are expected between frames */ }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch {
        if (!cancelled) setCamState("denied");
      }
    })();
    return () => { cancelled = true; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function stop() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }

  function finish(tag) {
    stop();
    onOpenChange(false);
    onResult(tag);
  }

  function submitManual(e) {
    e.preventDefault();
    const tag = tagFromScan(manual);
    if (tag) finish(tag);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) stop(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="scan-tag-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ScanLine className="w-4 h-4" /> Scan asset QR
          </DialogTitle>
          <DialogDescription className="text-xs">
            Point the camera at the label's QR code, or type the GB Asset ID below.
          </DialogDescription>
        </DialogHeader>

        {camState === "scanning" || camState === "starting" ? (
          <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
            <video ref={videoRef} playsInline muted className="w-full h-full object-cover"
              data-testid="scan-tag-video" />
            <div className="absolute inset-0 pointer-events-none border-2 border-white/60 m-10 rounded-lg" />
            {camState === "starting" && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-xs gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Starting camera…
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 flex items-center gap-2 text-xs text-slate-500"
            data-testid="scan-tag-no-camera">
            <CameraOff className="w-4 h-4 text-slate-400" />
            {camState === "unsupported"
              ? "In-app scanning isn't supported on this browser — scan with your phone camera, or type the code below."
              : "Camera unavailable or permission denied — type the GB Asset ID below."}
          </div>
        )}

        <form onSubmit={submitManual} className="flex items-center gap-2 pt-1">
          <Input value={manual} onChange={(e) => setManual(e.target.value)}
            placeholder="e.g. DMN-0357A5" className="h-8 text-xs"
            data-testid="scan-tag-manual-input" />
          <Button type="submit" size="sm" className="h-8 text-xs" disabled={!manual.trim()}
            data-testid="scan-tag-manual-go">Find</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
